import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  Link,
  Grid
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { getAuth, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { getFirestore, doc, setDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { HotelCodeService, WebUserService, HotelService } from '../services/firebaseService';

interface RegisterModalProps {
  open: boolean;
  onClose: () => void;
  onSwitchToLogin: () => void;
}

export const RegisterModal: React.FC<RegisterModalProps> = ({
  open,
  onClose,
  onSwitchToLogin
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    company: '',
    phone: '',
    hotelAdminUsername: '',
    hotelAdminPassword: '',
    hotelAdminConfirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const [generatedHotelCode, setGeneratedHotelCode] = useState<string>('');

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (formData.hotelAdminPassword !== formData.hotelAdminConfirmPassword) {
      setError('Hotel admin passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.hotelAdminPassword.length < 6) {
      setError('Hotel admin password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (!formData.hotelAdminUsername || formData.hotelAdminUsername.length < 3) {
      setError('Hotel admin username must be at least 3 characters');
      setLoading(false);
      return;
    }

    try {
      const auth = getAuth();
      
      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        formData.email, 
        formData.password
      );
      
      const user = userCredential.user;
      
      // Update display name
      await updateProfile(user, {
        displayName: `${formData.firstName} ${formData.lastName}`
      });

      // Generate unique hotel code
      const hotelCode = await HotelCodeService.generateUniqueCode();
      console.log('Generated unique hotel code:', hotelCode);
      
      // Reserve the hotel code
      await HotelCodeService.reserveCode(hotelCode, user.uid);
      console.log('Hotel code reserved:', hotelCode);

      // Create webUser document using new service
      await WebUserService.createWebUser(user.uid, {
        profile: {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          company: formData.company,
          phone: formData.phone
        },
        hotelAdmin: {
          username: formData.hotelAdminUsername,
          password: formData.hotelAdminPassword // Will be hashed in service
        }
      }, hotelCode);
      
      console.log('WebUser created successfully');

      // Create hotel document using new service
      await HotelService.createHotel({
        name: formData.company || `${formData.firstName}'s Hotel`,
        address: '', // Can be added later in hotel setup
        serverUrl: '', // Will be set during hotel setup
        serverPort: undefined,
        isAdminInstance: false
      }, hotelCode);
      
      console.log('Hotel created successfully');
      
      setGeneratedHotelCode(hotelCode);
      console.log('User registered:', user.uid, 'Hotel code:', hotelCode);
      
      setSuccess(true);
      
      // Show success for 2 seconds, then redirect
      setTimeout(() => {
        navigate('/dashboard');
        handleClose();
      }, 2000);
      
    } catch (error: any) {
      console.error('Registration error:', error);

      // Provide more specific error messages
      let errorMessage = 'Registration failed';

      if (error.code) {
        switch (error.code) {
          case 'auth/email-already-in-use':
            errorMessage = 'This email is already registered. Please use a different email or login.';
            break;
          case 'auth/weak-password':
            errorMessage = 'Password is too weak. Please use at least 6 characters.';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Please enter a valid email address.';
            break;
          case 'auth/operation-not-allowed':
            errorMessage = 'Email/password registration is not enabled. Please contact support.';
            break;
          default:
            errorMessage = error.message || 'Registration failed. Please try again.';
        }
      } else {
        errorMessage = error.message || 'Registration failed. Please try again.';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      company: '',
      phone: '',
      hotelAdminUsername: '',
      hotelAdminPassword: '',
      hotelAdminConfirmPassword: ''
    });
    setError('');
    setSuccess(false);
    onClose();
  };

  if (success) {
    return (
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.95))',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(74, 144, 194, 0.2)',
            borderRadius: '16px',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
          }
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(4px)'
          }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h5" color="primary" gutterBottom>
            🎉 Registration Successful!
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your account has been created and you get a 7-day free trial.
          </Typography>
          
          {generatedHotelCode && (
            <Box sx={{ 
              p: 2, 
              bgcolor: 'primary.light', 
              borderRadius: 1, 
              mb: 2,
              border: '2px solid',
              borderColor: 'primary.main'
            }}>
              <Typography variant="h6" color="primary.contrastText" gutterBottom>
                Your Hotel Code
              </Typography>
              <Typography 
                variant="h4" 
                color="primary.contrastText" 
                sx={{ 
                  fontFamily: 'monospace', 
                  fontWeight: 'bold',
                  letterSpacing: '0.2em'
                }}
              >
                {generatedHotelCode}
              </Typography>
              <Typography variant="body2" color="primary.contrastText" sx={{ mt: 1 }}>
                Save this code! You'll need it to access your hotel management system.
              </Typography>
            </Box>
          )}
          
          <Typography variant="body2" color="text.secondary">
            Redirecting to Dashboard...
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.95))',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(74, 144, 194, 0.2)',
          borderRadius: '16px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
        }
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(4px)'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ color: '#ffffff' }}>Register for Rafilis MP</Typography>
          <IconButton onClick={handleClose} size="small" sx={{ color: '#ffffff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <form onSubmit={handleRegister}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={handleChange('firstName')}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={handleChange('lastName')}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={formData.password}
                onChange={handleChange('password')}
                required
                margin="normal"
                helperText="Minimum 6 characters"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange('confirmPassword')}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Name"
                value={formData.company}
                onChange={handleChange('company')}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={handleChange('phone')}
                margin="normal"
              />
            </Grid>

            {/* Hotel Admin Section */}
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" sx={{ mt: 3, mb: 1 }}>
                Hotel Management Account
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create admin credentials for your hotel management system. This will be your main login for the hotel control panel.
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Hotel Admin Username"
                value={formData.hotelAdminUsername}
                onChange={handleChange('hotelAdminUsername')}
                required
                margin="normal"
                helperText="This will be used to login to your hotel management system"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Hotel Admin Password"
                type="password"
                value={formData.hotelAdminPassword}
                onChange={handleChange('hotelAdminPassword')}
                required
                margin="normal"
                helperText="Minimum 6 characters"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Confirm Hotel Admin Password"
                type="password"
                value={formData.hotelAdminConfirmPassword}
                onChange={handleChange('hotelAdminConfirmPassword')}
                required
                margin="normal"
              />
            </Grid>
          </Grid>
          
          <Box mt={2} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Link
                component="button"
                type="button"
                onClick={onSwitchToLogin}
                sx={{ cursor: 'pointer' }}
              >
                Login here
              </Link>
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            fullWidth
            sx={{ ml: 1 }}
          >
            {loading ? 'Creating Account...' : 'Start 7-Day Free Trial'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};